import { useState } from 'react';
import { useUser } from '../contexts';
import { isAdminEmail } from '../utils/admin';

export interface AdminListingMenuState {
	visible: boolean;
	x: number;
	y: number;
	/** Bounds of the element the menu was opened on, for positioning. */
	listingRect?: DOMRect;
}

export interface UseAdminListingMenuResult {
	isAdmin: boolean;
	menu: AdminListingMenuState;
	openMenu: (e: React.MouseEvent) => void;
	closeMenu: () => void;
}

const CLOSED: AdminListingMenuState = { visible: false, x: 0, y: 0 };

/**
 * Right-click admin menu state for anything that represents one listing -- a
 * grid tile, the product page's main photo.
 *
 * The gate lives here so every caller gets the same one: a non-admin never
 * opens the menu, so the native browser menu is left alone and nothing admin
 * ever renders for them. That is presentation only -- the feature, partner and
 * archive routes each check `isAdminEmail` against the server session, so a
 * logged-in shopper who forces this open still gets a 403.
 *
 * @param anchorRef element whose rect the menu positions against (optional).
 */
export function useAdminListingMenu(
	anchorRef?: React.RefObject<HTMLElement | null>,
): UseAdminListingMenuResult {
	const { profile } = useUser();
	const isAdmin = isAdminEmail(profile?.email);
	const [menu, setMenu] = useState<AdminListingMenuState>(CLOSED);

	const openMenu = (e: React.MouseEvent) => {
		if (!isAdmin) return;

		e.preventDefault();
		e.stopPropagation();

		setMenu({
			visible: true,
			x: e.clientX,
			y: e.clientY,
			listingRect: anchorRef?.current?.getBoundingClientRect() || undefined,
		});
	};

	const closeMenu = () => setMenu(CLOSED);

	return { isAdmin, menu, openMenu, closeMenu };
}

export default useAdminListingMenu;
